/**
 * Console breadcrumbs for the EloView screens.
 *
 * The WebView renderer on the wallpads dies without a stack trace (VIU-701).
 * The only trace that survives is the console: WebViewController copies it to
 * logcat with millisecond timestamps. These lines say what the page was doing
 * in the second before a `crash detected` line.
 */

const PREFIX = '[viu]';
const MAX_LINES_PER_SECOND = 20;
const MUTED_FLUSH_INTERVAL_MS = 200;
const MAX_ERROR_LENGTH = 300;
const SCROLL_LINE_INTERVAL_MS = 2000;
const HEAP_LINE_INTERVAL_MS = 60 * 1000;

type Log = (line: string) => void;

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
}

const toMb = (bytes: number): number => Math.round(bytes / (1024 * 1024));

const describeNode = (node: Node | null | undefined): string => {
  if (!node) {
    return '?';
  }
  if (node.nodeType === Node.TEXT_NODE) {
    // Length and parent only: the text itself (names, notices) stays out of
    // logcat, which EloView uploads to its cloud.
    const length = (node.textContent ?? '').length;
    return `#text(${length})<${describeNode(node.parentElement)}`;
  }
  if (node instanceof Element) {
    const id = node.id ? `#${node.id}` : '';
    const classes = (node.getAttribute('class') ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((c) => `.${c}`)
      .join('');
    return `${node.tagName.toLowerCase()}${id}${classes}`;
  }
  return node.nodeName;
};

/**
 * Last part of a URL without its query and fragment (signed URLs carry tokens
 * there); a data URI is cut to its type, the payload says nothing.
 */
const tail = (value: string, max: number): string => {
  if (value.startsWith('data:')) {
    return value.slice(0, value.indexOf(',') + 1 || max);
  }
  const path = value.split(/[?#]/, 1)[0];
  return path.length > max ? `…${path.slice(-max)}` : path;
};

/** One line per batch of DOM mutations: counts plus the first changed node. */
export const summarizeMutations = (records: MutationRecord[]): string => {
  let added = 0;
  let removed = 0;
  let attributes = 0;
  let text = 0;
  let first: Node | null | undefined;

  for (const record of records) {
    if (record.type === 'childList') {
      added += record.addedNodes.length;
      removed += record.removedNodes.length;
      first = first ?? record.addedNodes[0] ?? record.removedNodes[0];
    } else if (record.type === 'attributes') {
      attributes += 1;
      first = first ?? record.target;
    } else {
      text += 1;
      first = first ?? record.target;
    }
  }

  return `${PREFIX} dom +${added} -${removed} ~${attributes} t${text} ${describeNode(first)}`;
};

interface LimitedLog {
  log: Log;
  dispose: () => void;
}

/**
 * Caps the output so a runaway page cannot flood logcat. Above the cap the
 * lines are collected and the newest one is written every 200 ms with the
 * count of lines dropped before it, so the tail of a burst is never more than
 * 200 ms behind the crash it precedes. Error lines skip the cap.
 */
const createLimitedLog = (log: Log, now: () => number): LimitedLog => {
  let windowStart = now();
  let linesInWindow = 0;
  let muted = 0;
  let newestMuted = '';
  let flushTimer: ReturnType<typeof setTimeout> | undefined;

  const clearFlushTimer = (): void => {
    if (flushTimer !== undefined) {
      clearTimeout(flushTimer);
      flushTimer = undefined;
    }
  };

  const flush = (): void => {
    clearFlushTimer();
    if (muted === 0) {
      return;
    }
    log(muted > 1 ? `${newestMuted} (+${muted - 1} muted)` : newestMuted);
    muted = 0;
  };

  const limited: Log = (line) => {
    const t = now();
    if (t - windowStart >= 1000) {
      flush();
      windowStart = t;
      linesInWindow = 0;
    }
    linesInWindow += 1;
    if (linesInWindow > MAX_LINES_PER_SECOND) {
      muted += 1;
      newestMuted = line;
      if (flushTimer === undefined) {
        flushTimer = setTimeout(flush, MUTED_FLUSH_INTERVAL_MS);
      }
      return;
    }
    log(line);
  };

  return { log: limited, dispose: clearFlushTimer };
};

export interface CrashBreadcrumbOptions {
  log?: Log;
  now?: () => number;
}

let uninstallCurrent: (() => void) | undefined;

/**
 * Starts logging breadcrumbs. Installing twice is a no-op; the returned
 * function stops the logging again.
 */
export const installCrashBreadcrumbs = (
  options: CrashBreadcrumbOptions = {},
): (() => void) => {
  if (uninstallCurrent) {
    return uninstallCurrent;
  }

  const now = options.now ?? (() => Date.now());
  const rawLog = options.log ?? ((line: string) => console.log(line));
  // Errors bypass the cap: they are the lines that matter most when a
  // mutation burst has used up the budget.
  const limited = createLimitedLog(rawLog, now);
  const log = limited.log;

  const memory = (performance as Performance & { memory?: MemoryInfo }).memory;
  const heapText = (): string =>
    memory
      ? `heap ${toMb(memory.usedJSHeapSize)}/${toMb(memory.totalJSHeapSize)} MB`
      : 'heap n/a';

  log(
    `${PREFIX} boot ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio} ${heapText()}`,
  );

  const observer = new MutationObserver((records) => {
    log(summarizeMutations(records));
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
  });

  const onImageEvent = (event: Event): void => {
    if (event.target instanceof HTMLImageElement) {
      log(`${PREFIX} img ${event.type} ${tail(event.target.src, 60)}`);
    }
  };

  const lastScrollLine = new WeakMap<Element, number>();
  const onScroll = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const t = now();
    const last = lastScrollLine.get(target) ?? -Infinity;
    if (t - last < SCROLL_LINE_INTERVAL_MS) {
      return;
    }
    lastScrollLine.set(target, t);
    log(
      `${PREFIX} scroll ${describeNode(target)} left=${Math.round(target.scrollLeft)} top=${Math.round(target.scrollTop)}`,
    );
  };

  const onError = (event: ErrorEvent): void => {
    rawLog(`${PREFIX} error ${event.message.slice(0, MAX_ERROR_LENGTH)}`);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    rawLog(
      `${PREFIX} unhandledrejection ${String(event.reason).slice(0, MAX_ERROR_LENGTH)}`,
    );
  };
  const onVisibility = (): void => {
    log(`${PREFIX} visibility ${document.visibilityState}`);
  };
  const onOnline = (): void => {
    log(`${PREFIX} online`);
  };
  const onOffline = (): void => {
    log(`${PREFIX} offline`);
  };

  document.addEventListener('load', onImageEvent, true);
  document.addEventListener('error', onImageEvent, true);
  document.addEventListener('scroll', onScroll, true);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  const heapTimer = setInterval(
    () => log(`${PREFIX} ${heapText()}`),
    HEAP_LINE_INTERVAL_MS,
  );

  uninstallCurrent = () => {
    observer.disconnect();
    clearInterval(heapTimer);
    limited.dispose();
    document.removeEventListener('load', onImageEvent, true);
    document.removeEventListener('error', onImageEvent, true);
    document.removeEventListener('scroll', onScroll, true);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    uninstallCurrent = undefined;
  };

  return uninstallCurrent;
};
